import GUI, {type IGUIProps} from "../GUI"
import React, { ForwardedRef, useRef, useState, MouseEvent, useEffect, useImperativeHandle, forwardRef } from "react"
import Select, { TPrimitiveId } from "../Select/Select"
import { usePath } from "../../libs/path/PathProvider"
import MenuButton from "../Menu/MenuButton"
import MenuIcon from "../Icons/MenuIcon"
import { UpdateEvent } from "../GUIEvents"
import { TMenuProps, TMenuRow, useMenu } from "../Menu/Menu"
import { TAnchorProps } from "../Overlay/AnchoredBackdrop"
import Measure, { IMeasure } from "../Measure/Measure"

export {type TMenuRow }
type TTabMenuProps<ID extends TPrimitiveId> = {
    disabled?:boolean
    options:TMenuRow<ID>[]
    menuIcon?:JSX.Element
    anchorProps?:TAnchorProps
    menuProps?:Omit<TMenuProps<ID>, "options">
    titleRenderer?:(option:TMenuRow<ID>) => JSX.Element
    onUpdate?:(e:UpdateEvent<ID, HTMLDivElement>) => void
}
export type TTabsProps<ID extends TPrimitiveId> = IGUIProps & TTabMenuProps<ID> & {
    value?:ID
    defaultValue?:ID
    collapsable?:boolean
    disabled?:boolean
    onTab?:(id:ID) => void
    anchorProps?:TAnchorProps
    tabProps?:(option:TMenuRow<ID>) => Record<string,any>
    tabRenderer?:(option:TMenuRow<ID>) => React.ReactNode
    preIcon?:React.ReactNode
}

export interface ITabs<ID extends TPrimitiveId> {
    value:ID|null
}

function TabMenu<ID extends TPrimitiveId>(props:TTabMenuProps<ID>&{value:ID|null}) {
    const menuProps : TMenuProps<ID> = {options:props.options, ...props.menuProps}
    const defaultValue = props.value===null ? undefined : props.value
    return <div className="menuTitle flex middle">
        <MenuButton className="icon" anchorProps={props.anchorProps} menuProps={menuProps} onUpdate={props.onUpdate} defaultValue={defaultValue} disabled={props.disabled}>{props.menuIcon || <MenuIcon/>}</MenuButton>
    </div>
}

function Tabs<ID extends TPrimitiveId>(props:TTabsProps<ID>, ref?:ForwardedRef<ITabs<ID>>) {
    const [value,setValue] = useState<ID|null>(props.defaultValue !==undefined ? props.defaultValue : props.value === undefined ? null : props.value)
    const [busy,setBusy] = useState<boolean>(false)
    const refContainer = useRef<IMeasure|null>(null)
    const mounted = useRef<boolean>(false)
    const {selectMenu} = useMenu<ID>()

    const _onTab = (tab:TMenuRow<ID>) => {
        const id = Select.id(tab)
        props.onTab?.(id)
        setValue(id)
        selectMenu(tab)
    }
    const _setValue = async (id:ID) => {
        if (!refContainer.current?.container || busy) return

        const from = value!==null ? Select.indexOf(props.options, value) : -1
        const to = Select.indexOf(props.options, id)
        if (to<0 || from==to) return

        _onTab(Select.option(props.options, id))

        const tabs = refContainer.current.container.querySelectorAll(".tab")
        const tabFrom = tabs[from]
        const tabTo = tabs[to]
        if (tabFrom && tabTo) {
            setBusy(true)
            const rFrom = tabFrom.getBoundingClientRect(), rTo = tabTo.getBoundingClientRect()
            const border = tabTo.querySelector(".border") as HTMLDivElement;
            border.style.setProperty("width", tabFrom.clientWidth + "px")
            border.style.setProperty("left", (rFrom.left - rTo.left) + "px")
            await GUI.tick()
            if (!refContainer.current) return
            border.classList.add("animate")
            await GUI.tick()
            if (!refContainer.current) return
            border.style.setProperty("width", "100%")
            border.style.setProperty("left", "0")
            await GUI.transitionEnd(border)
            if (!refContainer.current) return
            border.classList.remove("animate")
        }
        setBusy(false)
    }

    const onClick = (option:TMenuRow<ID>) => (e:MouseEvent<HTMLDivElement>) => _setValue(Select.id(option))
    const onUpdate = (e:UpdateEvent<ID,HTMLDivElement>) => e.value!==undefined && e.value!==null && _onTab(Select.option(props.options, e.value))
    useEffect(() => {
        if (mounted.current) props.value !== undefined && _setValue(props.value)
        else mounted.current = true
    }, [props.value])

    useImperativeHandle(ref, () => ({
        get value() { return value}
    }))

    const tabs = props.options.map(option => {
        const {className,...tabProps} = props.tabProps?.(option) || {}
        const tab = props.tabRenderer?.(option) || Select.text(option)
        const id = Select.id(option)
        return <div className={GUI.joinClass("tab", className, value==id ? "selected" : "")} {...tabProps} key={id} onClick={onClick(option)}>{tab}<div className="border"/></div>
    })

    const title = value ? props.titleRenderer?.(value) ?? Select.text(Select.option(props.options, value)) : null
    const menuButton = props.collapsable 
        ? <div className="flex middle between gap-8 compact"><div className="title flex1">{title}</div><TabMenu options={props.options} anchorProps={props.anchorProps} menuIcon={props.menuIcon} value={value} onUpdate={onUpdate} disabled={props.disabled}/></div>
        : null
    return <Measure className={GUI.joinClass(props.className, "tabs", props.disabled ? "disabled" : "")} overflowContent={menuButton} ref={refContainer}>
        {props.preIcon ? props.preIcon : null}
        {tabs}
    </Measure>
}

export default forwardRef(Tabs) as <ID extends TPrimitiveId>(props:TTabsProps<ID> & {ref?:ForwardedRef<ITabs<ID>>}) => JSX.Element